import React, { useEffect, useRef, useState } from "react";
import Box from "@amzn/meridian/esm/components/box";
import Button from "@amzn/meridian/esm/components/button";
import Icon from "@amzn/meridian/esm/components/icon";
import Link from "@amzn/meridian/esm/components/link";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Theme from "@amzn/meridian/esm/components/theme";
import closeSmallTokens from "@amzn/meridian-tokens/base/icon/close-small";
import { THEME_COLOR } from "../../styles/colors";
import LogManager from "../../utils/logManager";
import sparklesIcon from "../../images/sparkles_small.svg";

const STORAGE_KEY = "aims_verbosity_banner_dismissed";

/**
 * Generic engagement metrics for the "new feature" banner.
 */
const IMPRESSION_METRIC = "NewFeatureBanner_Impression";
const DISMISS_METRIC = "NewFeatureBanner_Dismiss";

/**
 * Discovery banner nudging users to set their summary detail level in Settings.
 * Matches the existing "started by" banner pattern (teal border Box with dismiss).
 * Dismissible via sessionStorage (won't reappear during same session).
 *
 * Emits an impression metric when it becomes visible and a dismiss metric when the user closes it,
 * so we can tell how many people the banner reached and how many acknowledged it.
 *
 * @param {function} onSettingsClick - Callback to navigate to Settings tab
 */
const VerbosityDiscoveryBanner = ({ onSettingsClick }) => {
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(STORAGE_KEY) === "true"
  );
  // The banner unmounts/remounts as summarizeState changes within a meeting, and re-renders on
  // every parent state change. Emit at most one impression per mount so the count tracks people
  // rather than render churn.
  const impressionEmitted = useRef(false);

  useEffect(() => {
    if (dismissed || impressionEmitted.current) return;
    impressionEmitted.current = true;
    LogManager.metric(IMPRESSION_METRIC, 1);
  }, [dismissed]);

  if (dismissed) return null;

  const handleDismiss = () => {
    LogManager.metric(DISMISS_METRIC, 1);
    sessionStorage.setItem(STORAGE_KEY, "true");
    setDismissed(true);
  };

  return (
    <Row alignmentHorizontal="center" spacing="none">
      <div style={{ position: "relative" }}>
        <Theme tokens={{ boxBorderColorOutline: THEME_COLOR.LIGHT_TEAL }}>
          <Box type="outline" width="322px" spacingInset="400">
            <Row spacing="300" alignmentVertical="center">
              <img src={sparklesIcon} alt="" width="16" height="16" />
              <Row spacing="200" alignmentVertical="center">
                <Text type="b200">Set summary detail level in</Text>
                <Theme tokens={{ textColorPrimary: THEME_COLOR.TEAL_GREEN }}>
                  <Link onClick={onSettingsClick} type="secondary">
                    <Text color="primary" type="b200"><u>Settings</u></Text>
                  </Link>
                </Theme>
              </Row>
            </Row>
          </Box>
        </Theme>
        <div style={{ position: "absolute", top: "2px", right: "2px" }}>
          <Button type="icon" size="small" onClick={handleDismiss}>
            <Icon tokens={closeSmallTokens} color={THEME_COLOR.MID_GRAY}>Close</Icon>
          </Button>
        </div>
      </div>
    </Row>
  );
};

export default VerbosityDiscoveryBanner;
